import React from "react";
import { StyleSheet, View } from "react-native";
import { ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomProgressBar from "../../../../../components/common/general/CustomProgressBar/CustomProgressBar";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import type { CourseEvaluatorReviewMockData } from "../mockData/courseEvaluatorReview.mockData";
import { colors } from "../../../../../styles/design-system";

interface ReviewTopBarProps {
  review: CourseEvaluatorReviewMockData;
  reviewedProgress: number;
  onExitReview: () => void;
}

function ReviewTopBar({ review, reviewedProgress, onExitReview }: ReviewTopBarProps) {
  return (
    <View style={styles.topBar}>
      <View style={styles.topBarLeft}>
        <CustomButton
          text="Exit Review"
          styleType="transparent"
          size="s"
          useTranslationText={false}
          onPress={onExitReview}
          style={styles.exitButton}
          textStyle={styles.exitButtonText}
          leftIcon={<ArrowLeftIcon size={18} color={colors.grey[700]} />}
        />

        <View style={styles.topBarDivider} />

        <View style={styles.titleRow}>
          <CustomText
            text={`${review.courseName} (${review.sourceLabel})`}
            useTranslationText={false}
            size="m"
            weight="bold"
            role="heading"
            aria-level="1"
            style={styles.topBarTitle}
          />
          <CustomText text={review.courseCode} useTranslationText={false} size="s" style={styles.topBarMeta} />
          <CustomText
            text={review.termLabel}
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.topBarMeta}
          />
        </View>
      </View>

      <View style={styles.topBarRight}>
        <CustomText
          text={`${review.reviewedCount}/${review.totalCount} reviewed`}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.reviewedText}
        />
        <View style={styles.topProgressWrap}>
          <CustomProgressBar
            ariaLabel="Review progress"
            progress={reviewedProgress}
            color={colors.grey[300]}
            unfilledColor={colors.grey[100]}
          />
        </View>
        <CustomButton
          text={`Ready to Publish (${review.queuedCount})`}
          useTranslationText={false}
          onPress={() => {}}
          disabled
          styleType="lightStroke"
          size="s"
          rightIcon={<ArrowRightIcon size={16} color={colors.grey[400]} />}
          style={styles.publishButton}
          textStyle={styles.publishButtonText}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  topBar: {
    height: 56,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
  },
  topBarLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },
  exitButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  exitButtonText: {
    color: colors.grey[700],
    fontSize: 12,
    fontWeight: "500",
  },
  topBarDivider: {
    width: 1,
    height: 24,
    backgroundColor: colors.grey[200],
    marginHorizontal: 16,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    flex: 1,
  },
  topBarTitle: {
    color: colors.grey[900],
  },
  topBarMeta: {
    color: colors.grey[500],
  },
  topBarRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginLeft: 24,
  },
  reviewedText: {
    color: colors.grey[700],
  },
  topProgressWrap: {
    width: 80,
  },
  publishButton: {
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderColor: colors.grey[200],
    shadowColor: "transparent",
    elevation: 0,
  },
  publishButtonText: {
    color: colors.grey[400],
    fontSize: 12,
    fontWeight: "500",
  },
});

export default ReviewTopBar;
